import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Add } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import { getCurrentOrgId, getToken, getProfile } from 'client/modules/Auth/AuthReducer';
import {
  confirmDomainVerification as confirmDomainVerificationAction,
  fetchOrganization as fetchOrganizationAction,
  removeDomainVerification as removeDomainVerificationAction,
  startDomainVerification as startDomainVerificationAction,
  updateOrganization as updateOrganizationAction,
} from 'client/modules/Organizations/OrganizationActions';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

const FIELD_LABEL_MIN_WIDTH = 84;
// Centralized styles used in the DNS modal
const sxStyles = {
  infoText: { mb: 3, color: 'text.secondary' },
  instructionsTitle: { mb: 2, fontWeight: 'bold' },
  recordContainer: { bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 3 },
  labelRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 1,
  },
  labelRowWithTopMargin: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 1,
    mt: 2,
  },
  codeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    py: 1.25,
  },
  fieldRowDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    py: 1.25,
    borderTop: '1px solid',
    borderColor: 'divider',
    mt: 0.5,
  },
  fieldLabel: { fontWeight: 'bold', minWidth: FIELD_LABEL_MIN_WIDTH },
  codeBlock: {
    color: 'text.secondary',
    bgcolor: 'grey.50',
    display: 'block',
    borderRadius: 0.5,
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    wordBreak: 'break-all',
    flexGrow: 1,
  },
  hintRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    pl: `${FIELD_LABEL_MIN_WIDTH + 8}px`,
    color: 'warning.main',
    mt: 0.5,
  },
};

const messages = defineMessages({
  title: {
    id: 'Settings.SSO.Title',
    defaultMessage: 'Single Sign-On',
  },
  description: {
    id: 'Settings.SSO.Description',
    defaultMessage: 'Configure SSO enforcement, provisioning, allowed providers, and domain verification.',
  },
  domain: {
    id: 'Settings.SSO.Domain',
    defaultMessage: 'Primary domain',
  },
  addDomain: {
    id: 'Settings.SSO.AddDomain',
    defaultMessage: 'Add domain',
  },
  remove: {
    id: 'Settings.SSO.Remove',
    defaultMessage: 'Remove',
  },
  dnsName: {
    id: 'Settings.SSO.DNSName',
    defaultMessage: 'TXT name',
  },
  dnsValue: {
    id: 'Settings.SSO.DNSValue',
    defaultMessage: 'TXT value',
  },
  startVerification: {
    id: 'Settings.SSO.StartVerification',
    defaultMessage: 'Generate DNS TXT',
  },
  verifyNow: {
    id: 'Settings.SSO.VerifyNow',
    defaultMessage: 'Verify now',
  },
  dnsInstructions: {
    id: 'Settings.SSO.DNSInstructions',
    defaultMessage: 'Create a DNS TXT record with the name and value below, then click Verify.',
  },
  dnsType: {
    id: 'Settings.SSO.DNSType',
    defaultMessage: 'Type',
  },
  enforced: {
    id: 'Settings.SSO.Enforced',
    defaultMessage: 'Enforce SSO for this organization',
  },
  allowedProviders: {
    id: 'Settings.SSO.AllowedProviders',
    defaultMessage: 'Allowed identity providers',
  },
  verifiedDomains: {
    id: 'Settings.SSO.VerifiedDomains',
    defaultMessage: 'Verified domains',
  },
  domainColumn: {
    id: 'Settings.SSO.DomainColumn',
    defaultMessage: 'Domain',
  },
  statusColumn: {
    id: 'Settings.SSO.StatusColumn',
    defaultMessage: 'Status',
  },
  actionsColumn: {
    id: 'Settings.SSO.ActionsColumn',
    defaultMessage: 'Actions',
  },
  showDNS: {
    id: 'Settings.SSO.ShowDNS',
    defaultMessage: 'Show DNS',
  },
  verify: {
    id: 'Settings.SSO.Verify',
    defaultMessage: 'Verify',
  },
  defaultRole: {
    id: 'Settings.SSO.DefaultRole',
    defaultMessage: 'Default role for new users',
  },
  provisioningTitle: {
    id: 'Settings.SSO.ProvisioningTitle',
    defaultMessage: 'Provisioning',
  },
  provisioningMode: {
    id: 'Settings.SSO.ProvisioningMode',
    defaultMessage: 'Provisioning mode',
  },
  provisioningNone: {
    id: 'Settings.SSO.ProvisioningNone',
    defaultMessage: 'None',
  },
  provisioningJit: {
    id: 'Settings.SSO.ProvisioningJIT',
    defaultMessage: 'Just-in-time (JIT)',
  },
  provisioningScimSoon: {
    id: 'Settings.SSO.ProvisioningSCIMSoon',
    defaultMessage: 'SCIM (soon)',
  },
  save: {
    id: 'Settings.SSO.Save',
    defaultMessage: 'Save settings',
  },
  // New error messages for DNS verification
  txtRecordNotFound: {
    id: 'Settings.SSO.TXTRecordNotFound',
    defaultMessage:
      'DNS TXT record not found. Please ensure you have created the DNS TXT record with the exact name and value, then wait a few minutes for DNS propagation before trying again.',
  },
  invalidTxtValue: {
    id: 'Settings.SSO.InvalidTXTValue',
    defaultMessage:
      'Invalid DNS TXT value. Please check that the TXT record contains the exact value shown above, including any special characters.',
  },
  dnsVerificationFailed: {
    id: 'Settings.SSO.DNSVerificationFailed',
    defaultMessage: 'DNS verification failed. Please check your DNS configuration and try again.',
  },
  noVerificationInProgress: {
    id: 'Settings.SSO.NoVerificationInProgress',
    defaultMessage:
      'No domain verification in progress. Please start a new verification process first.',
  },
  domainRequired: {
    id: 'Settings.SSO.DomainRequired',
    defaultMessage: 'Domain is required. Please enter a valid domain name.',
  },
  organizationNotFound: {
    id: 'Settings.SSO.OrganizationNotFound',
    defaultMessage: 'Organization not found. Please refresh the page and try again.',
  },
  insufficientPermissions: {
    id: 'Settings.SSO.InsufficientPermissions',
    defaultMessage:
      'Insufficient permissions. Only organization owners and admins can manage domain verification.',
  },
  dnsResolutionFailed: {
    id: 'Settings.SSO.DNSResolutionFailed',
    defaultMessage:
      'DNS resolution failed. This could be due to network issues, DNS server problems, or the domain not being accessible. Please try again later.',
  },
  dnsVerificationSteps: {
    id: 'Settings.SSO.DNSVerificationSteps',
    defaultMessage: 'DNS setup for {domain}',
  },
  dnsVerificationDescription: {
    id: 'Settings.SSO.DNSVerificationDescription',
    defaultMessage:
      'To verify ownership of your domain, you need to add a TXT record to your DNS settings. We will check that record to confirm you own the domain. This process usually takes less than an hour, but can take up to 48 hours. You can close this window while you wait.',
  },
  dnsVerificationInstructions: {
    id: 'Settings.SSO.DNSVerificationInstructions',
    defaultMessage: 'Create a record in your DNS settings with these values:',
  },
  dnsPropagationNote: {
    id: 'Settings.SSO.DNSPropagationNote',
    defaultMessage: 'DNS changes can take up to 48 hours to propagate globally.',
  },
  dnsVerificationDelegationTitle: {
    id: 'Settings.SSO.DNSVerificationDelegationTitle',
    defaultMessage: 'Need someone else to make this update?',
  },
  dnsVerificationDelegationText: {
    id: 'Settings.SSO.DNSVerificationDelegationText',
    defaultMessage:
      'To update your DNS records, you need to log in to your domain provider and change your settings. If you need help with this, ask your IT team or site admin.',
  },
  copyValue: {
    id: 'Settings.SSO.CopyValue',
    defaultMessage: 'Copy',
  },
  copiedValue: {
    id: 'Settings.SSO.CopiedValue',
    defaultMessage: 'Copied!',
  },
  copyName: {
    id: 'Settings.SSO.CopyName',
    defaultMessage: 'Copy name',
  },
  regenerateToken: {
    id: 'Settings.SSO.RegenerateToken',
    defaultMessage: 'Regenerate token',
  },
  dnsValueHidden: {
    id: 'Settings.SSO.DNSValueHidden',
    defaultMessage: 'Hidden. Regenerate to reveal a new value.',
  },
  valueShownOnce: {
    id: 'Settings.SSO.ValueShownOnce',
    defaultMessage: 'For security, this value is only shown once. Copy it now.',
  },
  progressStep1: {
    id: 'Settings.SSO.ProgressStep1',
    defaultMessage: 'DNS TXT Generated',
  },
  progressStep2: {
    id: 'Settings.SSO.ProgressStep2',
    defaultMessage: 'Configuration',
  },
  close: {
    id: 'Settings.SSO.Close',
    defaultMessage: 'Close',
  },
  domainVerified: {
    id: 'Settings.SSO.DomainVerified',
    defaultMessage: 'Domain verified',
  },
  domainRemoved: {
    id: 'Settings.SSO.DomainRemoved',
    defaultMessage: 'Domain removed',
  },
  domainNotFound: {
    id: 'Settings.SSO.DomainNotFound',
    defaultMessage: 'Domain not found or already removed',
  },
  failedToStartVerification: {
    id: 'Settings.SSO.FailedToStartVerification',
    defaultMessage: 'Failed to start verification',
  },
  failedToRemoveDomain: {
    id: 'Settings.SSO.FailedToRemoveDomain',
    defaultMessage: 'Failed to remove domain',
  },
  // New error messages for SSO configuration saving
  ssoConfigurationFailed: {
    id: 'Settings.SSO.ConfigurationFailed',
    defaultMessage: 'Failed to save SSO configuration',
  },
  ssoValidationFailed: {
    id: 'Settings.SSO.ValidationFailed',
    defaultMessage: 'SSO configuration validation failed',
  },
  ssoPermissionDenied: {
    id: 'Settings.SSO.PermissionDenied',
    defaultMessage: 'Permission denied. Only organization owners and admins can modify SSO settings.',
  },
  ssoInvalidConfiguration: {
    id: 'Settings.SSO.InvalidConfiguration',
    defaultMessage: 'Invalid SSO configuration. Please check your settings and try again.',
  },
  ssoNetworkError: {
    id: 'Settings.SSO.NetworkError',
    defaultMessage: 'Network error. Please check your connection and try again.',
  },
  ssoLoadFailed: {
    id: 'Settings.SSO.LoadFailed',
    defaultMessage: 'Failed to load SSO settings',
  },
  ssoReadOnlyTooltip: {
    id: 'Settings.SSO.ReadOnlyTooltip',
    defaultMessage: 'Only organization owners and admins can modify SSO settings',
  },
  verificationTokenGenerated: {
    id: 'Settings.SSO.VerificationTokenGenerated',
    defaultMessage: 'Verification token generated',
  },
  statusVerified: {
    id: 'Settings.SSO.StatusVerified',
    defaultMessage: 'Verified',
  },
  statusPending: {
    id: 'Settings.SSO.StatusPending',
    defaultMessage: 'Pending',
  },
  cancel: {
    id: 'Common.Cancel',
    defaultMessage: 'Cancel',
  },
  domainPlaceholder: {
    id: 'Settings.SSO.DomainPlaceholder',
    defaultMessage: 'example.com',
  },
  providerGoogle: {
    id: 'Settings.SSO.ProviderGoogle',
    defaultMessage: 'Google',
  },
  providerMicrosoftSoon: {
    id: 'Settings.SSO.ProviderMicrosoftSoon',
    defaultMessage: 'Microsoft (soon)',
  },
  roleAdmin: {
    id: 'Settings.SSO.RoleAdmin',
    defaultMessage: 'Admin',
  },
  roleMember: {
    id: 'Settings.SSO.RoleMember',
    defaultMessage: 'Member',
  },
  roleViewer: {
    id: 'Settings.SSO.RoleViewer',
    defaultMessage: 'Viewer',
  },
  saved: {
    id: 'Common.Saved',
    defaultMessage: 'Saved',
  },
});

export default function SSOSettingsPage() {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const token = useSelector(getToken);
  const currentOrgId = useSelector(getCurrentOrgId);
  const profile = useSelector(getProfile);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [organization, setOrganization] = useState(null);
  const [domainInput, setDomainInput] = useState('');
  const [dnsRecord, setDnsRecord] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [dnsOpen, setDnsOpen] = useState(false);
  const [dnsDomain, setDnsDomain] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [copiedValue, setCopiedValue] = useState(false);
  const [copiedName, setCopiedName] = useState(false);
  const [menuDomain, setMenuDomain] = useState('');
  const [verifications, setVerifications] = useState([]);
  const [regenerating, setRegenerating] = useState(false);

  const [sso, setSso] = useState({
    enforced: false,
    allowedProviders: ['google'],
    allowedDomains: [],
    defaultRole: 'member',
  });
  const [provisioning, setProvisioning] = useState({ mode: 'jit' });

  // Permission checking logic
  const getCurrentUserRole = useCallback(() => {
    if (!organization || !profile?.user_id) return null;
    const member = organization.members?.find(
      m => m.user === profile.user_id || m.user?._id === profile.user_id,
    );
    return member?.role;
  }, [organization, profile?.user_id]);

  const canModifySSO = useMemo(() => {
    const role = getCurrentUserRole();
    return role === 'owner' || role === 'admin';
  }, [getCurrentUserRole]);

  const getPermissionTooltip = useCallback((action) => {
    if (canModifySSO) return '';
    return formatMessage(messages.ssoReadOnlyTooltip);
  }, [canModifySSO]);

  useEffect(() => {
    const load = async () => {
      if (!currentOrgId || !token) return;
      try {
        setLoading(true);
        clearAllAlerts();
        const res = await dispatch(fetchOrganizationAction(token, currentOrgId));
        const org = res.organization || {};
        setOrganization(org);
        const nextSso = {
          enforced: org?.sso?.enforced || false,
          allowedProviders: org?.sso?.allowedProviders?.length
            ? org.sso.allowedProviders
            : ['google'],
          allowedDomains: org?.sso?.allowedDomains || [],
          defaultRole: org?.sso?.defaultRole || 'member',
        };
        setSso(nextSso);
        setProvisioning({ mode: org?.provisioning?.mode || 'jit' });
        setDomainInput('');
        setVerifications(org?.domainVerifications || []);
      } catch (e) {
        // Handle specific loading errors with better user messages
        const errorMessage = e.bodyJson?.error || e.message;
        
        if (errorMessage === 'Organization not found') {
          setErrorWithDelay(formatMessage(messages.organizationNotFound));
        } else if (errorMessage === 'Insufficient permissions') {
          setErrorWithDelay(formatMessage(messages.ssoPermissionDenied));
        } else if (
          errorMessage &&
          (errorMessage.includes('ENOTFOUND') ||
            errorMessage.includes('ETIMEDOUT') ||
            errorMessage.includes('ECONNREFUSED') ||
            errorMessage.includes('network') ||
            errorMessage.includes('Network'))
        ) {
          setErrorWithDelay(formatMessage(messages.ssoNetworkError));
        } else if (errorMessage) {
          setErrorWithDelay(errorMessage);
        } else {
          setErrorWithDelay(formatMessage(messages.ssoLoadFailed));
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentOrgId, token]);

  // Enhanced auto-dismiss logic with better state management
  const clearError = useCallback(() => setError(null), []);
  const clearSuccess = useCallback(() => setSuccess(null), []);
  
  const clearAllAlerts = useCallback(() => {
    setError(null);
    setSuccess(null);
  }, []);
  
  const setErrorWithDelay = useCallback((message) => {
    clearAllAlerts();
    // Small delay to ensure previous alerts are cleared before setting new ones
    setTimeout(() => setError(message), 100);
  }, [clearAllAlerts]);
  
  const setSuccessWithDelay = useCallback((message) => {
    clearAllAlerts();
    // Small delay to ensure previous alerts are cleared before setting new ones
    setTimeout(() => setSuccess(message), 100);
  }, [clearAllAlerts]);

  useEffect(() => {
    let errorTimer;
    let successTimer;

    if (error) {
      errorTimer = setTimeout(() => {
        setError(null);
      }, 5000);
    }

    if (success) {
      successTimer = setTimeout(() => {
        setSuccess(null);
      }, 5000);
    }

    return () => {
      if (errorTimer) clearTimeout(errorTimer);
      if (successTimer) clearTimeout(successTimer);
    };
  }, [error, success]);

  const onToggleProvider = provider => e => {
    const checked = e.target.checked;
    setSso(prev => {
      const set = new Set(prev.allowedProviders);
      if (checked) set.add(provider);
      else set.delete(provider);
      return { ...prev, allowedProviders: Array.from(set) };
    });
  };

  const onSave = async () => {
    try {
      clearAllAlerts();
      await dispatch(
        updateOrganizationAction(token, currentOrgId, {
          sso: {
            enforced: !!sso.enforced,
            allowedProviders: sso.allowedProviders,
            allowedDomains: verifications.filter(v => v.verifiedAt).map(v => v.domain),
            defaultRole: sso.defaultRole,
          },
          provisioning: { mode: provisioning.mode },
        }),
      );
      setSuccessWithDelay(formatMessage(messages.saved));
    } catch (e) {
      // Handle specific SSO configuration errors with better user messages
      const errorMessage = e.bodyJson?.error || e.message;
      
      if (errorMessage === 'Organization not found') {
        setErrorWithDelay(formatMessage(messages.organizationNotFound));
      } else if (errorMessage === 'Insufficient permissions') {
        setErrorWithDelay(formatMessage(messages.ssoPermissionDenied));
      } else if (errorMessage === 'Validation failed' || errorMessage?.includes('validation')) {
        setErrorWithDelay(formatMessage(messages.ssoValidationFailed));
      } else if (errorMessage === 'Invalid configuration' || errorMessage?.includes('invalid')) {
        setErrorWithDelay(formatMessage(messages.ssoInvalidConfiguration));
      } else if (
        errorMessage &&
        (errorMessage.includes('ENOTFOUND') ||
          errorMessage.includes('ETIMEDOUT') ||
          errorMessage.includes('ECONNREFUSED') ||
          errorMessage.includes('network') ||
          errorMessage.includes('Network'))
      ) {
        setErrorWithDelay(formatMessage(messages.ssoNetworkError));
      } else if (errorMessage) {
        setErrorWithDelay(errorMessage);
      } else {
        setErrorWithDelay(formatMessage(messages.ssoConfigurationFailed));
      }
    }
  };

  const startVerification = async domainOverride => {
    try {
      clearAllAlerts();
      const d = (domainOverride || domainInput).trim().toLowerCase();
      const res = await dispatch(
        startDomainVerificationAction(token, currentOrgId, d),
      );
      setDnsRecord(res.dns);
      setDnsDomain(d);
      setDnsOpen(true);
      setSuccessWithDelay(formatMessage(messages.verificationTokenGenerated));
      // refresh current list
      const orgRes = await dispatch(fetchOrganizationAction(token, currentOrgId));
      setVerifications(orgRes.organization?.domainVerifications || []);
    } catch (e) {
      // Handle specific domain verification errors with better user messages
      const errorMessage = e.bodyJson?.error || e.message;
      if (errorMessage === 'Domain is required') {
        setErrorWithDelay(formatMessage(messages.domainRequired));
      } else if (errorMessage === 'Organization not found') {
        setErrorWithDelay(formatMessage(messages.organizationNotFound));
      } else if (errorMessage === 'Insufficient permissions') {
        setErrorWithDelay(formatMessage(messages.insufficientPermissions));
      } else {
        setErrorWithDelay(errorMessage || formatMessage(messages.failedToStartVerification));
      }
    }
  };

  const confirmVerification = async domainOverride => {
    try {
      clearAllAlerts();
      const d = (domainOverride || dnsDomain || domainInput).trim().toLowerCase();
      await dispatch(
        confirmDomainVerificationAction(token, currentOrgId, d),
      );
      setSuccessWithDelay(formatMessage(messages.domainVerified));
      const orgRes = await dispatch(fetchOrganizationAction(token, currentOrgId));
      setVerifications(orgRes.organization?.domainVerifications || []);
      setDnsOpen(false);
      clearAllAlerts();
    } catch (e) {
      // Handle specific DNS verification errors with better user messages
      const errorMessage = e.bodyJson?.error || e.message;
      if (errorMessage === 'TXT record not found') {
        setErrorWithDelay(formatMessage(messages.txtRecordNotFound));
      } else if (errorMessage === 'Invalid TXT value') {
        setErrorWithDelay(formatMessage(messages.invalidTxtValue));
      } else if (errorMessage === 'No verification in progress') {
        setErrorWithDelay(formatMessage(messages.noVerificationInProgress));
      } else if (errorMessage === 'Domain is required') {
        setErrorWithDelay(formatMessage(messages.domainRequired));
      } else if (errorMessage === 'Organization not found') {
        setErrorWithDelay(formatMessage(messages.organizationNotFound));
      } else if (errorMessage === 'Insufficient permissions') {
        setErrorWithDelay(formatMessage(messages.insufficientPermissions));
      } else if (
        errorMessage &&
        (errorMessage.includes('ENOTFOUND') ||
          errorMessage.includes('ETIMEDOUT') ||
          errorMessage.includes('DNS'))
      ) {
        setErrorWithDelay(formatMessage(messages.dnsResolutionFailed));
      } else {
        setErrorWithDelay(errorMessage || formatMessage(messages.dnsVerificationFailed));
      }
    }
  };

  const removeDomain = async domain => {
    try {
      clearAllAlerts();
      await dispatch(
        removeDomainVerificationAction(token, currentOrgId, domain),
      );
      setSuccessWithDelay(formatMessage(messages.domainRemoved));
      const orgRes = await dispatch(fetchOrganizationAction(token, currentOrgId));
      setVerifications(orgRes.organization?.domainVerifications || []);
    } catch (e) {
      // Handle specific domain verification errors with better user messages
      const errorMessage = e.bodyJson?.error || e.message;
      if (errorMessage === 'Domain is required') {
        setErrorWithDelay(formatMessage(messages.domainRequired));
      } else if (errorMessage === 'Organization not found') {
        setErrorWithDelay(formatMessage(messages.organizationNotFound));
      } else if (errorMessage === 'Insufficient permissions') {
        setErrorWithDelay(formatMessage(messages.insufficientPermissions));
      } else if (errorMessage === 'Domain not found') {
        setErrorWithDelay(formatMessage(messages.domainNotFound));
      } else {
        setErrorWithDelay(errorMessage || formatMessage(messages.failedToRemoveDomain));
      }
    }
  };

  const copyToClipboard = async text => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedValue(true);
      setTimeout(() => setCopiedValue(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedValue(true);
      setTimeout(() => setCopiedValue(false), 2000);
    }
  };

  const copyNameToClipboard = async text => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedName(true);
      setTimeout(() => setCopiedName(false), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedName(true);
      setTimeout(() => setCopiedName(false), 2000);
    }
  };

  const allowedDomainsText = useMemo(
    () => (sso.allowedDomains || []).join(', '),
    [sso.allowedDomains],
  );

  if (loading) return null;

  return (
    <Box>
      {!dnsOpen && error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {error}
        </Alert>
      )}
      {!dnsOpen && success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={clearSuccess}>
          {success}
        </Alert>
      )}

      <Typography variant="subtitle1" gutterBottom>
        {formatMessage(messages.verifiedDomains)}
      </Typography>

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: 'background.body' }}>
            <TableRow>
              <TableCell>{formatMessage(messages.domainColumn)}</TableCell>
              <TableCell>{formatMessage(messages.statusColumn)}</TableCell>
              <TableCell align="right">
                <Tooltip title={getPermissionTooltip()}>
                  <span>
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() => setAddOpen(true)}
                      startIcon={<Add />}
                      disabled={!canModifySSO}
                    >
                      {formatMessage(messages.addDomain)}
                    </Button>
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{ '& .MuiTableRow-root:last-of-type .MuiTableCell-root': { borderBottom: 0 } }}
          >
            {verifications.map(v => (
              <TableRow key={v.domain}>
                <TableCell>{v.domain}</TableCell>
                <TableCell>
                  {v.verifiedAt
                    ? formatMessage(messages.statusVerified)
                    : formatMessage(messages.statusPending)}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={e => {
                      setMenuAnchor(e.currentTarget);
                      setMenuDomain(v.domain);
                    }}
                    aria-label="actions"
                    title={formatMessage(messages.actionsColumn)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Card variant="outlined">
        <CardContent>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!sso.enforced}
                  onChange={e => setSso(prev => ({ ...prev, enforced: e.target.checked }))}
                  disabled={!canModifySSO}
                />
              }
              label={formatMessage(messages.enforced)}
            />
          </FormGroup>

          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <FormControl size="small">
              <InputLabel>{formatMessage(messages.provisioningMode)}</InputLabel>
              <Select
                label={formatMessage(messages.provisioningMode)}
                value={provisioning.mode}
                onChange={e => setProvisioning({ mode: e.target.value })}
                sx={{ minWidth: 260 }}
                disabled={!canModifySSO}
              >
                <MenuItem value="none">{formatMessage(messages.provisioningNone)}</MenuItem>
                <MenuItem value="jit">{formatMessage(messages.provisioningJit)}</MenuItem>
                <MenuItem value="scim" disabled>
                  {formatMessage(messages.provisioningScimSoon)}
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            {formatMessage(messages.allowedProviders)}
          </Typography>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={sso.allowedProviders.includes('google')}
                  onChange={onToggleProvider('google')}
                  disabled={!canModifySSO}
                />
              }
              label={formatMessage(messages.providerGoogle)}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={sso.allowedProviders.includes('microsoft')}
                  onChange={onToggleProvider('microsoft')}
                  disabled
              />
              }
              label={formatMessage(messages.providerMicrosoftSoon)}
            />
          </FormGroup>

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <FormControl size="small">
              <InputLabel>{formatMessage(messages.defaultRole)}</InputLabel>
              <Select
                label={formatMessage(messages.defaultRole)}
                value={sso.defaultRole}
                onChange={e => setSso(prev => ({ ...prev, defaultRole: e.target.value }))}
                sx={{ minWidth: 200 }}
                disabled={!canModifySSO}
              >
                <MenuItem value="admin">{formatMessage(messages.roleAdmin)}</MenuItem>
                <MenuItem value="member">{formatMessage(messages.roleMember)}</MenuItem>
                <MenuItem value="viewer">{formatMessage(messages.roleViewer)}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Tooltip title={getPermissionTooltip()}>
              <span>
                <Button 
                  variant="contained" 
                  onClick={onSave}
                  disabled={!canModifySSO}
                >
                  {formatMessage(messages.save)}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>

      {/* Add Domain Modal */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{formatMessage(messages.addDomain)}</DialogTitle>
        <DialogContent>
          <TextField
            label={formatMessage(messages.domain)}
            value={domainInput}
            onChange={e => setDomainInput(e.target.value)}
            placeholder={formatMessage(messages.domainPlaceholder)}
            fullWidth
            size="small"
            disabled={!canModifySSO}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>{formatMessage(messages.cancel)}</Button>
          <Tooltip title={getPermissionTooltip()}>
            <span>
              <Button
                variant="contained"
                onClick={async () => {
                  await startVerification();
                  setAddOpen(false);
                }}
                disabled={!canModifySSO}
              >
                {formatMessage(messages.addDomain)}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      {/* DNS Modal */}
      <Dialog open={dnsOpen} onClose={() => {
        setDnsOpen(false);
        clearAllAlerts();
      }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {formatMessage(messages.showDNS)} - {dnsDomain}
        </DialogTitle>
        <DialogContent>
          {(error || success) && (
            <Box sx={{ mb: 2 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 1 }} onClose={clearError}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert severity="success" onClose={clearSuccess}>
                  {success}
                </Alert>
              )}
            </Box>
          )}
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={sxStyles.infoText}>
                {formatMessage(messages.dnsVerificationDescription)}
              </Typography>

              <Typography variant="subtitle2" sx={sxStyles.instructionsTitle}>
                {formatMessage(messages.dnsVerificationInstructions)}
              </Typography>

              <Box sx={sxStyles.recordContainer}>
                {/* Type row */}
                <Box sx={sxStyles.fieldRow}>
                  <Typography variant="body2" sx={sxStyles.fieldLabel}>
                    {formatMessage(messages.dnsType)}:
                  </Typography>
                  <Typography variant="body2" component="code" sx={sxStyles.codeBlock}>
                    TXT
                  </Typography>
                </Box>

                {/* Name row */}
                <Box sx={sxStyles.fieldRowDivider}>
                  <Typography variant="body2" sx={sxStyles.fieldLabel}>
                    {formatMessage(messages.dnsName)}:
                  </Typography>
                  <Box sx={sxStyles.codeRow}>
                    <Typography variant="body2" component="code" sx={sxStyles.codeBlock}>
                      {dnsRecord?.name || `_pn-verify.${dnsDomain}`}
                    </Typography>
                    <Tooltip
                      title={
                        copiedName
                          ? formatMessage(messages.copiedValue)
                          : formatMessage(messages.copyName)
                      }
                      arrow
                    >
                      <IconButton
                        size="small"
                        onClick={() =>
                          copyNameToClipboard(dnsRecord?.name || `_pn-verify.${dnsDomain}`)
                        }
                        aria-label={formatMessage(messages.copyName)}
                      >
                        <ContentCopyIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Value row */}
                <Box sx={sxStyles.fieldRowDivider}>
                  <Typography variant="body2" sx={sxStyles.fieldLabel}>
                    {formatMessage(messages.dnsValue)}:
                  </Typography>
                  <Box sx={sxStyles.codeRow}>
                    <Typography variant="body2" component="code" sx={sxStyles.codeBlock}>
                      {dnsRecord?.value || formatMessage(messages.dnsValueHidden)}
                    </Typography>
                    {dnsRecord?.value ? (
                      <Tooltip
                        title={
                          copiedValue
                            ? formatMessage(messages.copiedValue)
                            : formatMessage(messages.copyValue)
                        }
                        arrow
                      >
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(dnsRecord.value)}
                          aria-label={formatMessage(messages.copyValue)}
                        >
                          <ContentCopyIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={regenerating || !canModifySSO}
                        onClick={async () => {
                          try {
                            setRegenerating(true);
                            await startVerification(dnsDomain);
                          } finally {
                            setRegenerating(false);
                          }
                        }}
                        sx={{ minWidth: 'auto', px: 1.5 }}
                      >
                        {formatMessage(messages.regenerateToken)}
                      </Button>
                    )}
                  </Box>
                </Box>
                {dnsRecord?.value && (
                  <Box sx={sxStyles.hintRow}>
                    <WarningAmberIcon fontSize="inherit" />
                    <Typography variant="caption" sx={{ color: 'warning.main' }}>
                      {formatMessage(messages.valueShownOnce)}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}
                >
                  💡 {formatMessage(messages.dnsVerificationDelegationTitle)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatMessage(messages.dnsVerificationDelegationText)}
                </Typography>
              </Box>
            </Box>
          </>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDnsOpen(false);
            clearAllAlerts();
          }}>{formatMessage(messages.close)}</Button>
          <Tooltip title={getPermissionTooltip()}>
            <span>
              <Button 
                variant="contained" 
                onClick={() => confirmVerification(dnsDomain)}
                disabled={!canModifySSO}
              >
                {formatMessage(messages.verify)}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      {/* Row actions menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setDnsDomain(menuDomain);
            setDnsRecord(null);
            clearAllAlerts();
            setDnsOpen(true);
          }}
        >
          {formatMessage(messages.showDNS)}
        </MenuItem>
        <MenuItem
          onClick={async () => {
            setMenuAnchor(null);
            await confirmVerification(menuDomain);
          }}
          disabled={!canModifySSO}
        >
          {formatMessage(messages.verify)}
        </MenuItem>
        <MenuItem
          onClick={async () => {
            setMenuAnchor(null);
            await removeDomain(menuDomain);
          }}
          disabled={!canModifySSO}
        >
          {formatMessage(messages.remove)}
        </MenuItem>
      </Menu>
    </Box>
  );
}
